import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import { StaticModal } from '@nexelem/modals';
import { getTranslation } from '@nexelem/utils';
import { $getRoot, $isParagraphNode } from 'lexical';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '../../components/Divider';
import { IconButton } from '../../components/IconButton';

import { SPEECH_TO_TEXT_COMMAND, SUPPORT_SPEECH_RECOGNITION } from '../SpeechToTextPlugin';
import { ShowClearDialog } from './ShowClearDialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faTrash } from '@fortawesome/pro-light-svg-icons';

export default function ActionsPlugin() {
  const { t: translation } = useTranslation();
  const t = useMemo(() => getTranslation(translation, 'components.richTextEditor.actionsPlugin'), [translation]);
  const [editor] = useLexicalComposerContext();
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());
  const [isSpeechToText, setIsSpeechToText] = useState(false);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);

  useEffect(
    () =>
      mergeRegister(
        editor.registerEditableListener((editable) => {
          setIsEditable(editable);
        }),
      ),
    [editor],
  );

  useEffect(
    () =>
      editor.registerUpdateListener(({ dirtyElements, prevEditorState, tags }) => {
        editor.getEditorState().read(() => {
          const root = $getRoot();
          const children = root.getChildren();

          if (children.length > 1) {
            setIsEditorEmpty(false);
          } else {
            if ($isParagraphNode(children[0])) {
              const paragraphChildren = children[0].getChildren();
              setIsEditorEmpty(paragraphChildren.length === 0);
            } else {
              setIsEditorEmpty(false);
            }
          }
        });
      }),
    [editor, isEditable],
  );

  const onClickRecord = useCallback(() => {
    editor.dispatchCommand(SPEECH_TO_TEXT_COMMAND, !isSpeechToText);
    setIsSpeechToText(!isSpeechToText);
  }, [editor, isSpeechToText]);

  const onToggleClearModal = useCallback(() => {
    setIsClearModalOpen((prevState) => !prevState);
  }, []);

  return (
    <div className="flex">
      {/* temporary disable speech recognition */}
      {SUPPORT_SPEECH_RECOGNITION && false && (
        <>
          <IconButton
            ariaLabel={t(isSpeechToText ? 'speech.enabled' : 'speech.disabled')}
            isActive={isSpeechToText}
            StartIcon={<FontAwesomeIcon icon={faMicrophone} />}
            title={t('speech.label')}
            variant="text"
            onClick={onClickRecord}
          />
          <Divider />
        </>
      )}
      <IconButton
        ariaLabel={t('clear.ariaLabel')}
        disabled={isEditorEmpty}
        StartIcon={<FontAwesomeIcon icon={faTrash} />}
        title={t('clear.label')}
        variant="text"
        onClick={onToggleClearModal}
      />
      <StaticModal isOpen={isClearModalOpen} onClose={onToggleClearModal}>
        <ShowClearDialog editor={editor} onClose={onToggleClearModal} />
      </StaticModal>
    </div>
  );
}
